<template>
  <div class="flex flex-col">
    <h2>Planes</h2>

    <custom-search
      class="mt-2 w-full sm:w-[500px]"
      placeholder="Buscar por cliente, mascota o #plan"
      v-model="searchText"
      @confirm="search"
      @clear="search"
    />

    <div class="flex flex-col lg:flex-row gap-4 lg:gap-6 mt-4 items-start">
      <status-side-nav :options="statusNavOptions" />

      <div class="flex-1 min-w-0 w-full">
        <div v-if="!loading" class="card">
          <table class="table-auto text-sm mt-2 bg-white w-full">
            <thead class="text-left">
              <tr class="bg-gray-100">
                <th class="p-2 rounded-l-lg">Plan</th>
                <th class="p-2">Cliente</th>
                <th class="p-2">Mascotas</th>
                <th class="p-2">Entrega</th>
                <th class="p-2">Pago</th>
                <th class="p-2">Siguiente pago</th>
                <th class="p-2 rounded-r-lg">Acciones</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="quote in quotes" :key="quote.id" class="table-row">
                <td class="table-cell">
                  <p class="font-medium">#{{ quote.id }}</p>
                  <p class="text-xs text-content-400">{{ formatDate(quote.created_at) }}</p>
                </td>
                <td class="table-cell">
                  <router-link
                    v-if="quote.client"
                    :to="{ name: 'clientsDetail', params: { id: quote.client.id } }"
                    class="detail-link"
                    >{{ quote.client.name }} {{ quote.client.last_name }}</router-link
                  >
                  <span v-else class="text-content-400">Cliente eliminado</span>
                </td>
                <td class="table-cell">
                  <div class="flex flex-wrap gap-x-3">
                    <span
                      v-for="diet in activeDiets(quote)"
                      :key="diet.id"
                      :class="{ 'line-through text-content-400': diet.pet && diet.pet.deleted_at }"
                      >{{ diet.pet ? diet.pet.name : "—" }}</span
                    >
                    <span v-if="!activeDiets(quote).length" class="text-content-400">Sin dietas activas</span>
                  </div>
                </td>
                <td class="table-cell text-xs">
                  {{ quote.current_pickup_delivery ? "Pick-Up" : "Envío" }}
                  <span v-if="quote.delivery_frequency" class="block text-content-400">
                    {{ quote.delivery_frequency.name }}
                  </span>
                </td>
                <td class="table-cell text-xs">
                  <span v-if="quote.is_cash_plan">Efectivo</span>
                  <span v-else-if="quote.courtesy">Cortesía</span>
                  <span v-else>Tarjeta</span>
                  <span v-if="quote.payment_frequency" class="block text-content-400">
                    {{ quote.payment_frequency.name }}
                  </span>
                </td>
                <td class="table-cell text-xs">
                  {{ quote.next_payment_date ? formatDate(quote.next_payment_date) : "—" }}
                </td>
                <td class="table-cell">
                  <router-link
                    :to="{ name: 'quotesDetail', params: { id: quote.id } }"
                    class="secondary-button"
                    >Detalle</router-link
                  >
                </td>
              </tr>
              <tr v-if="!quotes.length">
                <td colspan="7" class="text-center p-4 text-content-400">
                  No hay planes {{ currentStatusLabel.toLowerCase() }} en esta sucursal.
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <div v-else class="flex w-full h-40 items-center">
          <loader-icon class="h-10 w-10 text-primary-500 mx-auto"></loader-icon>
        </div>

        <pagination
          :current-page="currentPage"
          :total-pages="totalPages"
          @page-change="onPageChange"
        />
      </div>
    </div>
  </div>
</template>

<script>
import api from "@/api";
import Pagination from "@/components/ui/Pagination.vue";
import StatusSideNav from "@/components/ui/StatusSideNav";
import { formatDate } from "@/utils/dateFormatter";

const STATUS_OPTIONS = [
  { status: "pending", label: "Pendientes de pago", icon: "🕐", activeClass: "text-pending" },
  { status: "active", label: "Activos", icon: "✅", activeClass: "text-delivered" },
  { status: "paused", label: "Pausados", icon: "⏸️", activeClass: "text-inProcess" },
  { status: "suspended", label: "Suspendidos", icon: "⚠️", activeClass: "text-failed" },
  { status: "canceled", label: "Cancelados", icon: "❌", activeClass: "text-failed" },
];

export default {
  name: "Quotes",

  components: { Pagination, StatusSideNav },

  data() {
    return {
      quotes: [],
      loading: false,
      status: "active",
      searchText: "",
      currentPage: 1,
      totalPages: 0,
    };
  },

  computed: {
    statusNavOptions() {
      return STATUS_OPTIONS.map((option) => ({
        label: option.label,
        icon: option.icon,
        to: { name: "quotesIndex", query: { status: option.status, search: this.searchText || undefined } },
        active: this.status === option.status,
        activeClass: option.activeClass,
      }));
    },

    currentStatusLabel() {
      const option = STATUS_OPTIONS.find((o) => o.status === this.status);
      return option ? option.label : "";
    },
  },

  watch: {
    "$route.query.status"(value) {
      if (this.$route.name !== "quotesIndex") return;
      this.status = value || "active";
      this.currentPage = 1;
      this.loadQuotes();
    },
  },

  created() {
    this.$store.commit("setSelectedTab", "quotes");
    this.status = this.$route.query.status || "active";
    this.searchText = this.$route.query.search || "";
    this.loadQuotes();
  },

  methods: {
    formatDate,

    activeDiets(quote) {
      return (quote.diets || []).filter((d) => d.status === "active");
    },

    async loadQuotes() {
      this.loading = true;
      try {
        const response = await api.quotes.getByBranch(this.$route.params.branchId, [
          { name: "status", value: this.status },
          { name: "page", value: this.currentPage },
          { name: "search", value: this.searchText },
        ]);
        this.quotes = response.data || [];
        this.currentPage = response.current_page || 1;
        this.totalPages = response.last_page || 0;
      } catch (error) {
        this.$store.dispatch("errorSnack", error?.response?.data?.message);
      }
      this.loading = false;
    },

    search() {
      this.currentPage = 1;
      this.loadQuotes();
    },

    onPageChange(page) {
      this.currentPage = page;
      this.loadQuotes();
    },
  },
};
</script>
